import React from "react";
import { RecordModel } from "../core";
import { Badge, DatePicker, Tag, Tooltip } from "antd";
import { CalendarOutlined, UploadOutlined, BankOutlined, UserOutlined } from '@ant-design/icons';
import moment from "moment";
import { UnitModel } from "./housing";

// The platform's expense code catalog (`bookkeeping.ExpenseCode`). Read-only:
// it's seeded by migration and owned by the platform, so the API refuses writes.
export type IExpenseCode = {
  id: number;
  code: string;
  name_es: string;
  name_en: string;
  group: 'G' | 'M' | 'R' | 'F' | 'O';
  kind: 'cost' | 'operator_charge' | 'reserve';
  default_inventory_policy: 'holder' | 'operator' | 'none';
  is_active: boolean;
}

export type IExpenseStatement = {
  id: number;
  unit: number;
  unit_name: string;
  month: string;
  month_display: string;
  total_fractions: number;
  sold_fractions: number;
  balances_json: {
    expense_balance?: { initial: number; final: number };
    reserve_fund?: { initial: number; final: number };
  };
  expenses_json: Array<{
    code: string;
    description: string;
    total_amount: number;
    sold_fractions_amount: number;
    billable: boolean;
  }>;
  reserve_fund_json: Array<{
    type: string;
    amount: number;
    description: string;
    date: string;
  }>;
  expense_initial_balance: number;
  expense_final_balance: number;
  total_expenses: number;
  owner_statements: number[];
  created_at: string;
  modified_at: string;
  is_finalized: boolean;
}

export type IOwnerStatement = {
  id: number;
  expense_statement: number;
  unit_owner: number;
  unit_name: string;
  owner_name: string;
  owner_email: string;
  month_display: string;
  fraction_numbers: number[];
  fraction_count: number;
  balances_json: {
    expense_balance?: { initial: number; final: number };
    str_rental_balance?: { initial: number; final: number; total_income: number; total_fees: number };
  };
  expenses_json: Array<{
    description: string;
    property_total: number;
    fraction_amount: number;
    running_balance: number;
  }>;
  str_rental_json: Array<{
    type: string;
    amount: number;
    description: string;
    date: string;
  }>;
  expense_initial_balance: number;
  expense_final_balance: number;
  str_rental_balance: number;
  created_at: string;
  modified_at: string;
}

export const ExpenseCodeModel: RecordModel<IExpenseCode> = {
  name: "Expense Code",
  namePlural: "Expense Codes",
  path: "bookkeeping/expense-codes",
  selectQuery: "&fields=id,code,name_es",
  tableQuery: "&fields=id,code,name_es,name_en,group,kind,default_inventory_policy,is_active&page_size=100",
  toString: (r) => `${r.code} - ${r.name_es}`,
  customTableMappings: {
    is_active: {
      render: (record) => (
        <Badge
          status={record.is_active ? "success" : "error"}
          text={record.is_active ? "Active" : "Inactive"}
        />
      )
    }
  },
  allowSearch: true
}

export const ExpenseStatementModel: RecordModel<IExpenseStatement> = {
  name: "Expense Statement",
  namePlural: "Expense Statements",
  path: "expense-statements",
  selectQuery: "&fields=id,unit_name,month_display,expense_final_balance",
  tableQuery: "&fields=id,unit,unit_name,month,month_display,total_fractions,sold_fractions,expense_initial_balance,expense_final_balance,total_expenses,created_at,is_finalized",
  toString: (r) => `${r.unit_name} - ${r.month_display}`,
  icon: <CalendarOutlined />,
  customTableMappings: {
    unit: { omit: true },
    month: { omit: true },
    created_at: { omit: true },
    modified_at: { omit: true },
    owner_statements: { omit: true },
    expenses_json: { omit: true },
    reserve_fund_json: { omit: true },
    balances_json: { omit: true },
    month_display: {
      title: "Period",
      render: (record) => (
        <Tag icon={<CalendarOutlined />} color="blue">
          {moment(record.month_display).format('MMMM YYYY')}
        </Tag>
      )
    },
    unit_name: {
      title: "Property",
      render: (record) => (
        <Tag icon={<BankOutlined />} color="green">
          {record.unit_name}
        </Tag>
      )
    },
    total_fractions: {
      title: "Fractions",
      render: (record) => `${record.sold_fractions}/${record.total_fractions}`,
      width: 100
    },
    sold_fractions: { omit: true },
    expense_initial_balance: {
      title: "Initial Balance",
      render: (record) => (
        <span style={{ color: record.expense_initial_balance >= 0 ? '#52c41a' : '#ff4d4f' }}>
          ${record.expense_initial_balance?.toLocaleString() || '0'}
        </span>
      ),
      align: 'right'
    },
    expense_final_balance: {
      title: "Final Balance",
      render: (record) => (
        <span style={{ color: record.expense_final_balance >= 0 ? '#52c41a' : '#ff4d4f' }}>
          ${record.expense_final_balance?.toLocaleString() || '0'}
        </span>
      ),
      align: 'right'
    },
    total_expenses: {
      title: "Total Expenses",
      render: (record) => `$${record.total_expenses?.toLocaleString() || '0'}`,
      align: 'right'
    }
  },
  // allowSearch: true,
  pageSize: 25,
  detailAction: true,
  customActions: [
    /*
    {
      name: 'Import Excel (det)',
      path: 'import_from_excel_to_detail',
      icon: <UploadOutlined />,
      method: 'post',
      isDetail: true,
      hidden: () => true,
    },
    */
    {
      name: 'Import Excel',
      path: 'import_from_excel',
      icon: <UploadOutlined />,
      method: 'post',
    }
  ],
  tableFilters: [
    { selectModel: UnitModel },
    {
      component: ({ query, handleFilter }) => {
        const MonthPickerComponent = DatePicker.MonthPicker as any;
        return (
          <MonthPickerComponent
            defaultValue={!!query.get('month_year') ? moment(query.get('month_year')) : undefined}
            onChange={(v: any) => handleFilter('month_year', v ? v.startOf('month').format('YYYY-MM') : '')}
          />
        )
      }
    },
  ]
}

export const OwnerStatementModel: RecordModel<IOwnerStatement> = {
  name: "Owner Statement",
  namePlural: "Owner Statements",
  path: "owner-statements",
  selectQuery: "&fields=id,owner_name,month_display,expense_final_balance",
  tableQuery: "&fields=id,expense_statement,unit_owner,unit_name,owner_name,owner_email,month_display,fraction_numbers,fraction_count,expense_initial_balance,expense_final_balance,str_rental_balance,created_at",
  toString: (r) => `${r.owner_name} - ${r.month_display}`,
  icon: <UserOutlined />,
  customTableMappings: {
    expense_statement: { omit: true },
    unit_owner: { omit: true },
    owner_email: { omit: true },
    created_at: { omit: true },
    modified_at: { omit: true },
    balances_json: { omit: true },
    expenses_json: { omit: true },
    str_rental_json: { omit: true },
    month_display: {
      title: "Period",
      render: (record) => (
        <Tag icon={<CalendarOutlined />} color="blue">
          {record.month_display}
        </Tag>
      ),
      width: 120
    },
    unit_name: {
      title: "Property",
      render: (record) => (
        <Tag icon={<BankOutlined />} color="green">
          {record.unit_name}
        </Tag>
      ),
      width: 150
    },
    owner_name: {
      title: "Owner",
      render: (record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.owner_name}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.owner_email}</div>
        </div>
      )
    },
    fraction_numbers: {
      title: "Fractions",
      render: (record) => (
        <div>
          {record.fraction_numbers?.map(num => (
            <Tag key={num} color="purple">#{num}</Tag>
          ))}
        </div>
      ),
      width: 90
    },
    fraction_count: { omit: true },
    expense_initial_balance: {
      title: "Initial Balance",
      render: (record) => (
        <span style={{ color: record.expense_initial_balance >= 0 ? '#52c41a' : '#ff4d4f' }}>
          ${record.expense_initial_balance?.toLocaleString() || '0'}
        </span>
      ),
      align: 'right'
    },
    expense_final_balance: {
      title: "Final Balance",
      render: (record) => (
        <span style={{ color: record.expense_final_balance >= 0 ? '#52c41a' : '#ff4d4f' }}>
          ${record.expense_final_balance?.toLocaleString() || '0'}
        </span>
      ),
      align: 'right'
    },
    str_rental_balance: {
      title: "STR Balance",
      render: (record) => (
        <Tooltip title="Short-term rental balance">
          <span style={{ color: record.str_rental_balance >= 0 ? '#52c41a' : '#ff4d4f' }}>
            ${record.str_rental_balance?.toLocaleString() || '0'}
          </span>
        </Tooltip>
      ),
      align: 'right'
    }
  },
  tableFilters: [
    {
      component: ({ query, handleFilter }) => (
        <input
          type="number"
          placeholder="Year (e.g. 2024)"
          value={query.get('year') || ''}
          onChange={(e) => handleFilter('year', e.target.value)}
          style={{ width: 120 }}
        />
      )
    },
    {
      component: ({ query, handleFilter }) => (
        <input
          placeholder="Owner email"
          value={query.get('owner_email') || ''}
          onChange={(e) => handleFilter('owner_email', e.target.value)}
          style={{ width: 150 }}
        />
      )
    },
    {
      component: ({ query, handleFilter }) => (
        <input
          placeholder="Property name"
          value={query.get('unit_name') || ''}
          onChange={(e) => handleFilter('unit_name', e.target.value)}
          style={{ width: 150 }}
        />
      )
    }
  ],
  allowSearch: true,
  pageSize: 25,
  detailAction: true
}
